import type { FormStepper } from '@paid-ui/types/form';

import { forwardRef, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Link } from '@paid-ui/components/link';

import { SignUpStep } from '../../models/enums';

export interface StepFormFooterProps<T extends number = number>
  extends React.HTMLAttributes<HTMLDivElement> {
  steps: FormStepper<T>[];
  currentStep: T;
}

const StepFormFooter = forwardRef(
  <T extends number = number>(
    { steps, currentStep, hidden, className, ...restProps }: StepFormFooterProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [signInHref, setSignInHref] = useState('/signin');

    useEffect(() => {
      setSignInHref(`/signin${globalThis.location.search}`);
    }, []);

    if (hidden) {
      return null;
    }

    if (
      currentStep === SignUpStep.CREATE_CONTRACT ||
      currentStep === SignUpStep.COMPLETE_CONTRACT ||
      currentStep === SignUpStep.REVIEW_CONTRACT
    ) {
      const action =
        currentStep === SignUpStep.CREATE_CONTRACT ? 'create contract' : 'complete contract';

      return (
        <footer
          ref={ref}
          className={twMerge('shrink-0 text-center text-base', className)}
          {...restProps}
        >
          I will {action} later.{' '}
          <Link className="font-semibold" href="/">
            Go to dashboard
          </Link>
        </footer>
      );
    }

    return (
      <footer
        ref={ref}
        className={twMerge('shrink-0 text-center text-base text-grey', className)}
        {...restProps}
      >
        Already have an account?{' '}
        <Link className="font-medium" href={signInHref}>
          Sign in
        </Link>
        .
      </footer>
    );
  },
);

StepFormFooter.displayName = 'StepFormFooter';

export default StepFormFooter as <T extends number = number>(
  props: StepFormFooterProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => JSX.Element;
